import { Draggable, KeyboardSensor } from "@dnd-kit/dom";
import { PointerSensor, useDraggable } from "@dnd-kit/react";
import { useEvent } from "client-util/useEvent";
import { clsObj } from "client-util/util";
import Globals from "Globals";
import Tooltip from "components/Tooltip";
import { PuzzleLink } from "components/Link";
import { useMovableContext } from "components/Movables/MovableProvider";
import {
  getBaseRect,
  posRelTo,
  useMovableToRect,
} from "components/Movables/pos";
import { makeClickOrDispatch } from "components/Movables/util";
import Popover from "components/Popover";
import PuzzleContentDisplay from "components/PuzzleContent/PuzzleContentDisplay";
import { Region } from "components/Region";
import { motion } from "framer-motion";
import { useRef, useState } from "react";
import Twemoji from "react-twemoji";
import { PuzzleData, PuzzleMetadata } from "sockapi/puzzle-data";
import { ROUND_IDS, makeBoxPieceId } from "sockapi/scope-specs/round-spec";
import { CUSTOM_TITLE_MAX_LENGTH } from "sockapi/scope-specs/team-titles-spec";
import { ScopeType } from "sockapi/scope-types";
import {
  useBoxIds,
  usePiece,
  useUpdateMovable,
  useUpdatePieceInside,
} from "stores/RoundStore";
import {
  useGlobalScopeState,
  useScopeStateResolvable,
  usePuzzleData,
  usePuzzleDisplayNames,
  useCustomTitle,
} from "stores/ScopeStateStore";
import {
  useShouldDisplayAdmin,
  useTeamId,
} from "stores/ServerInteractionStore";
import "./PieceView.css";

export function PieceMenu({
  boxId,
  pieceId,
  getDragRef,
}: {
  boxId: string | null;
  pieceId: string;
  getDragRef?: () => HTMLElement;
}) {
  const inPiece = getDragRef === undefined;
  const { piece, boxIds } = usePiece(boxId, pieceId);
  const allBoxIds = useBoxIds();
  const updateInside = useUpdatePieceInside();
  const pieceData = usePuzzleData(pieceId);
  const displayNames = usePuzzleDisplayNames();
  const [isRenaming, setIsRenaming] = useState(false);
  const customTitle = useCustomTitle(pieceId, () => setIsRenaming(false));

  const updateMovable = useUpdateMovable();
  const commit = useEvent(async (props: Parameters<typeof updateMovable>[1]) =>
    updateMovable(makeBoxPieceId(boxId, pieceId), props)
  );

  if (!piece || !pieceData || !displayNames) return null;

  return (
    <div className="piece-menu">
      {!inPiece && <PuzzleLink puzName={pieceId} subpage="piece" />}
      {pieceData.region ? <Region region={pieceData.region} /> : null}
      <div className="piece-menu-top">
        {!inPiece && (
          <Tooltip text="View piece">
            <PuzzleLink puzName={pieceId} subpage="piece">
              <button style={{ cursor: "pointer" }}>
                <Twemoji>🔎</Twemoji>
              </button>
            </PuzzleLink>
          </Tooltip>
        )}
        {!inPiece && pieceData.displayName === "" && (
          <Popover
            open={isRenaming}
            setOpen={setIsRenaming}
            content={
              <input
                type="text"
                autoFocus
                maxLength={CUSTOM_TITLE_MAX_LENGTH}
                defaultValue={customTitle.value}
                disabled={customTitle.disableInput}
                onBlur={(e) => customTitle.set(e.currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.currentTarget.blur();
                  }
                }}
              />
            }
          >
            <Tooltip
              text={`${customTitle.value ? "Rename" : "Name"} this piece. This is just for your convenience and is shared with your team.`}
            >
              <button style={{ cursor: "pointer" }}>
                <Twemoji>✏️</Twemoji>
              </button>
            </Tooltip>
          </Popover>
        )}
        {getDragRef && (
          <Tooltip text="Copy to clipboard">
            <button
              className="clipboard-button"
              onClick={(e) => {
                Globals.handleClipboardButton(
                  getDragRef().querySelector(
                    ":scope .clipboard-container"
                  ) as HTMLElement,
                  e.currentTarget
                );
              }}
            >
              <Twemoji>📋️</Twemoji>
            </button>
          </Tooltip>
        )}
        <div className="button-squish-zone">
          {!inPiece && pieceData.allowRotation ? (
            <>
              <Tooltip text="Rotate counterclockwise">
                <button
                  onClick={() =>
                    commit({
                      rot: piece.rot + (piece.flipped ? 90 : -90),
                    })
                  }
                >
                  <Twemoji>↪️</Twemoji>
                </button>
              </Tooltip>
              <Tooltip text="Rotate clockwise">
                <button
                  onClick={() =>
                    commit({
                      rot: piece.rot + (piece.flipped ? -90 : 90),
                    })
                  }
                >
                  <Twemoji>↩️</Twemoji>
                </button>
              </Tooltip>
            </>
          ) : null}
          {!inPiece && pieceData.allowFlipping ? (
            <Tooltip text="Flip">
              <button onClick={() => commit({ flipped: !piece.flipped })}>
                <Twemoji>↔️</Twemoji>
              </button>
            </Tooltip>
          ) : null}
        </div>
      </div>
      <div className="piece-menu-inside">
        {boxIds.map((boxId) => {
          return (
            <div key={boxId}>
              <Tooltip text="Remove from puzzle">
                <button
                  onClick={() => {
                    updateInside({ pieceId, boxId, add: false }).catch(
                      console.error
                    );
                  }}
                >
                  <Twemoji>📤</Twemoji>
                </button>
              </Tooltip>
              <PuzzleLink puzName={boxId} type="puzzle" />
            </div>
          );
        })}
      </div>
      <label className="piece-menu-add">
        Add to:{" "}
        <select
          value={""}
          onChange={(e) => {
            if (e.target.value) {
              updateInside({ pieceId, boxId: e.target.value, add: true }).catch(
                console.error
              );
            }
          }}
        >
          <option value="">Select a puzzle...</option>
          {allBoxIds
            .filter((b) => !boxIds.includes(b))
            .map((boxId) => (
              <option key={boxId} value={boxId}>
                {displayNames[boxId]}
              </option>
            ))}
        </select>
      </label>
    </div>
  );
}

function PieceMeta({ puzData }: { puzData: PuzzleMetadata | PuzzleData }) {
  const teamId = useTeamId();
  const customName =
    useScopeStateResolvable(
      ScopeType.TEAM_TITLES,
      teamId !== null ? { teamId } : null
    )?.titles[puzData.puzName] ?? "";
  const shouldDisplayAdmin = useShouldDisplayAdmin();
  const content = [
    puzData.displayName,
    customName,
    shouldDisplayAdmin && "puzReadable" in puzData
      ? `[${puzData.puzReadable}]`
      : "",
  ].filter(Boolean);
  if (!content.length) return null;
  return (
    <Tooltip
      className="piece-meta no-copy"
      text={<Twemoji>{content.join(" ")}</Twemoji>}
    />
  );
}

export function PieceIconOverlay({
  puzData: { displayName, region, roundId },
}: {
  puzData: PuzzleMetadata;
}) {
  if (region !== undefined)
    return (
      <div className="piece-icon-overlay">
        <Region region={region} iconOnly={true} />
      </div>
    );
  if (roundId === ROUND_IDS.site)
    return (
      <div className="piece-icon-overlay">
        <Twemoji className="piece-icon-overlay-inner">{displayName}</Twemoji>
      </div>
    );
  return null;
}

export default function PieceView({
  boxId,
  pieceId,
  className,
}: {
  boxId: string | null;
  pieceId: string;
  className?: string;
}) {
  const { piece, box } = usePiece(boxId, pieceId);
  const ctx = useMovableContext();
  const toRect = useMovableToRect();
  const teamPuzzleData = usePuzzleData(pieceId);
  const shouldDisplayAdmin = useShouldDisplayAdmin();
  const huntAdminScopeState = useGlobalScopeState(
    ScopeType.HUNT_ADMIN,
    shouldDisplayAdmin
  );
  const puzData = huntAdminScopeState?.puzzles[pieceId] ?? teamPuzzleData;
  const dragRef = useRef<HTMLDivElement>(null);
  const clickOrDispatch = makeClickOrDispatch();

  const allowDrag = (sourceId: string, event: PointerEvent) => {
    if (
      puzData?.transparentBackground !== true ||
      sourceId !== makeBoxPieceId(boxId, pieceId)
    ) {
      return { allow: true, nextElt: null };
    }
    return clickOrDispatch(dragRef.current, event);
  };

  class CursedPointerSensor extends PointerSensor {
    protected override handlePointerDown(
      event: PointerEvent,
      source: Draggable,
      options = this.options
    ): void {
      const { allow, nextElt } = allowDrag(source.id as string, event);
      if (allow) {
        super.handlePointerDown(event, source, options);
      } else if (nextElt) {
        nextElt.dispatchEvent(new PointerEvent("pointerdown", event));
      }
    }
  }

  useDraggable({
    type: boxId ? "boxPiece" : "piece",
    id: makeBoxPieceId(boxId, pieceId),
    element: dragRef,
    feedback: "none",
    sensors: [
      ctx.type === "round" ? PointerSensor : CursedPointerSensor,
      KeyboardSensor,
    ],
  });

  const base = getBaseRect();
  if (!piece || !puzData || !base) return null;
  const rect = (() => {
    const pieceRect = toRect(piece, base);
    return ctx.type === "round" && box
      ? posRelTo(pieceRect, toRect(box, base))
      : posRelTo(pieceRect, base);
  })();

  return (
    <motion.div
      ref={dragRef}
      className={clsObj({
        "piece-view": true,
        ...(className !== undefined ? { [className]: true } : undefined),
        "piece-view-transparent": puzData.transparentBackground ?? false,
        uncontained: !boxId,
      })}
      initial={{ ...rect, opacity: 0 }}
      animate={{ ...rect, opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      <Popover
        ignoreTransparent={
          ctx.type === "puzzle" && puzData.transparentBackground
        }
        allowAudioClick={ctx.type === "puzzle" && puzData.hasAudio}
        content={
          <PieceMenu
            boxId={boxId}
            pieceId={pieceId}
            getDragRef={() => dragRef.current!}
          />
        }
        style={{
          position: "absolute",
          inset: 0,
        }}
      >
        <PieceMeta puzData={puzData} />
        <div className="piece-content">
          <PuzzleContentDisplay puzName={pieceId} isPreview />
        </div>
        <PieceIconOverlay puzData={puzData} />
      </Popover>
    </motion.div>
  );
}
